import { isCmoeShadowValidationEnabled } from "../../../config/cmoeFeatureFlags";
import { logCmoeSyncMismatch, logCmoeSyncError, logCmoeSyncInfo } from "../cmoeSyncLogger";
import { validateRoomSessionParity } from "./roomSessionParityValidator";
import { withCmoeTiming } from "./cmoePerformanceBaseline";

/**
 * @typedef {import('../../../domain/cmoe/shadow/parityTypes').ShadowOperation} ShadowOperation
 */

/**
 * V1 has no pause/resume dual-write yet — shadow hook reserved for future parity.
 * @param {ShadowOperation} operation
 */
export function isShadowOperationSupported(operation) {
  if (operation === "Pause" || operation === "Resume") return false;
  return true;
}

/**
 * Run parity validation after a successful dual-write (non-blocking for V1).
 * @param {ShadowOperation} operation
 * @param {Object} ctx
 * @param {string} ctx.roomCode
 * @param {string | null} [ctx.matchId]
 * @param {object | null} [ctx.roomDoc]
 * @param {import('../../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch[]} [ctx.roomMatches]
 */
export async function runShadowValidationAfterDualWrite(operation, ctx) {
  if (typeof isCmoeShadowValidationEnabled !== "function" || !isCmoeShadowValidationEnabled()) {
    return null;
  }
  if (process.env.NODE_ENV === "test") return null;
  if (!isShadowOperationSupported(operation)) {
    logCmoeSyncInfo("shadow_validation_skipped", {
      operation,
      reason: "v1_operation_not_dual_written",
    });
    return null;
  }
  try {
    const report = await withCmoeTiming(
      `shadow:${operation}`,
      () =>
        validateRoomSessionParity({
          roomCode: ctx.roomCode,
          roomDoc: ctx.roomDoc,
          roomMatches: ctx.roomMatches || [],
          operation,
          focusMatchId: ctx.matchId || null,
        }),
      { reads: 0 }
    );
    if (!report.ok) {
      logCmoeSyncMismatch("shadow_parity", {
        operation,
        roomCode: ctx.roomCode,
        matchId: ctx.matchId,
        mismatchCount: report.mismatchCount,
        dimensions: summarizeMismatches(report),
      });
    } else {
      logCmoeSyncInfo("shadow_parity_ok", {
        operation,
        roomCode: ctx.roomCode,
        matchId: ctx.matchId,
      });
    }
    return report;
  } catch (err) {
    logCmoeSyncError("shadow_validation_failed", err, {
      operation,
      roomCode: ctx.roomCode,
      matchId: ctx.matchId,
    });
    return null;
  }
}

/**
 * @param {import('../../../domain/cmoe/shadow/parityTypes').RoomSessionParityReport} report
 */
function summarizeMismatches(report) {
  const out = {};
  for (const [key, val] of Object.entries(report.dimensions || {})) {
    if (val && val.ok === false && !val.skipped) {
      out[key] = { expected: val.expected, actual: val.actual, details: val.details };
    }
  }
  return out;
}

/**
 * Fire-and-forget wrapper for dual-write paths.
 * @param {ShadowOperation} operation
 * @param {Parameters<typeof runShadowValidationAfterDualWrite>[1]} ctx
 */
export function scheduleShadowValidation(operation, ctx) {
  void runShadowValidationAfterDualWrite(operation, ctx);
}
